/**
 * @file legal-state.ts
 * @description Чистые правила состояния юридического документа (phase19.16,
 * SB139–SB143; проект данных docs/phases/phase19.16-materials/db-design-legal-docs.md
 * §1.3, §3.3, §4): состояние для пилюли кабинета, «виден ли на сайте»,
 * предупреждения, что мешает публикации, пустой ли текст, подпись размера
 * файла и очистка имени файла.
 *
 * Зачем в shared: кабинет выключает «Опубликовать» и показывает пилюлю по тем
 * же правилам, по которым API отвечает 422 и считает `visibleOnSite`, — иначе
 * кнопка активна, а сервер отказывает. Видимость в SQL (`legalVisibleWhere`
 * в api) — то же правило, что `isLegalDocumentVisible` здесь; тест api
 * сверяет их на сидах.
 *
 * Вход — поля строки `LegalDocument` в том виде, как их читает API (без Date:
 * даты здесь не нужны). Число видимых документов группы считает вызывающий
 * (у ручной группы — видимые члены, у группы сертификатов — действующие
 * публичные сертификаты с файлом).
 */

import { LEGAL_FILE_NAME_MAX_LENGTH } from "./legal-limits.js";
import { isLegalSystemSlug, isLegalTextOnlySlug } from "./legal-slugs.js";
import type {
  LegalDocumentKind,
  LegalDocumentStatus,
  LegalDocumentWarning,
  LegalErrorCode,
  LegalGroupSource,
} from "./legal-dto.js";

/** Поля документа, от которых зависит состояние. */
export interface LegalDocumentStateInput {
  kind: LegalDocumentKind;
  isPublished: boolean;
  liveRevisionId: string | null;
  hasDraftChanges: boolean;
  /** Только GROUP: сколько документов группы сейчас видно на сайте. */
  visibleMembers?: number;
}

/**
 * Состояние в кабинете (§1.3):
 *   TEXT/PDF — нет действующей редакции → черновик (в том числе «показывать»
 *   без редакции — данные до переноса: на сайте его нет); есть редакция и
 *   показ → «опубликован» или «есть правки»; редакция есть, показа нет →
 *   «снят» (редакции сохранены);
 *   GROUP — не показывается → черновик; показывается и есть видимые
 *   документы → «опубликована», иначе «пуста — на сайте не видна» (SB16).
 */
export function legalDocumentStatus(doc: LegalDocumentStateInput): LegalDocumentStatus {
  if (doc.kind === "GROUP") {
    if (!doc.isPublished) return "draft";
    return (doc.visibleMembers ?? 0) > 0 ? "published" : "empty";
  }
  if (doc.liveRevisionId === null) return "draft";
  if (!doc.isPublished) return "unpublished";
  return doc.hasDraftChanges ? "changed" : "published";
}

/**
 * Виден ли документ покупателю (= `legalVisibleWhere` в api): TEXT/PDF —
 * показ и есть действующая редакция; GROUP — показ и хотя бы один видимый
 * документ. Одно правило для меню, кнопок секций, своих строк подвала,
 * `pagePaths`, карты сайта и проверок SB125.
 */
export function isLegalDocumentVisible(doc: LegalDocumentStateInput): boolean {
  if (!doc.isPublished) return false;
  if (doc.kind === "GROUP") return (doc.visibleMembers ?? 0) > 0;
  return doc.liveRevisionId !== null;
}

/** Подпись состояния для пилюли: «Опубликован · ред. 4», «Опубликована · 3 док.». */
export function legalDocumentStatusLabel(
  status: LegalDocumentStatus,
  context: { kind: LegalDocumentKind; liveNumber?: number | null; visibleMembers?: number },
): string {
  const group = context.kind === "GROUP";
  const number = context.liveNumber ?? null;
  switch (status) {
    case "draft":
      return "Черновик";
    case "published":
      if (group) return `Опубликована · ${context.visibleMembers ?? 0} док.`;
      return number === null ? "Опубликован" : `Опубликован · ред. ${number}`;
    case "changed":
      return "Опубликован · есть правки";
    case "unpublished":
      return number === null ? "Снят с публикации" : `Снят (ред. ${number} сохранена)`;
    case "empty":
      return "Опубликована · на сайте не видна";
  }
}

// ── Текст ───────────────────────────────────────────────────────────────────

/** Символы нулевой ширины: невидимы, текстом не считаются. */
const ZERO_WIDTH: ReadonlySet<number> = new Set([0x200b, 0x200c, 0x200d, 0x2060]);

/** Пуст ли текст документа после снятия тегов, неразрывных пробелов и пробелов (422 `legal_text_empty`). */
export function isLegalTextEmpty(html: string | null | undefined): boolean {
  if (!html) return true;
  // Класс \s в JS покрывает и неразрывный пробел, и BOM; символы нулевой ширины — по коду.
  const stripped = html.replace(/<[^>]*>/g, " ").replace(/&nbsp;|&#160;|&#xa0;/gi, " ");
  return [...stripped].every((ch) => /\s/.test(ch) || ZERO_WIDTH.has(ch.codePointAt(0) ?? 0));
}

// ── Что мешает публикации ───────────────────────────────────────────────────

/** Поля черновика, от которых зависит публикация. */
export interface LegalPublishInput extends LegalDocumentStateInput {
  description: string | null;
  contentHtml: string | null;
  /** PDF: есть файл — новый файл черновика или файл действующей редакции. */
  hasFile: boolean;
}

/**
 * Почему «Опубликовать» не сработает — коды 422/409 API в порядке проверки
 * сервиса (§4). Пусто — можно публиковать. `legal_nothing_to_publish` —
 * правок нет и документ уже показан.
 */
export function legalPublishIssues(doc: LegalPublishInput): LegalErrorCode[] {
  const issues: LegalErrorCode[] = [];
  const noDescription = (doc.description ?? "").trim().length === 0;
  if (doc.kind === "GROUP") {
    if (noDescription) issues.push("legal_description_required");
    if ((doc.visibleMembers ?? 0) === 0) issues.push("legal_group_empty");
    return issues;
  }
  if (doc.liveRevisionId !== null && !doc.hasDraftChanges) {
    // Правок нет: снятый документ «показывается снова», видимый — публиковать нечего.
    if (doc.isPublished) issues.push("legal_nothing_to_publish");
    return issues;
  }
  if (doc.kind === "PDF") {
    if (!doc.hasFile) issues.push("legal_pdf_required");
    if (noDescription) issues.push("legal_description_required");
  } else if (isLegalTextEmpty(doc.contentHtml)) {
    issues.push("legal_text_empty");
  }
  return issues;
}

// ── Предупреждения ──────────────────────────────────────────────────────────

/** Поля документа для предупреждений списка и карточки. */
export interface LegalWarningsInput extends LegalDocumentStateInput {
  slug: string;
  description: string | null;
  groupSource?: LegalGroupSource | null;
  /** Только GROUP: сколько документов в группе всего (видимых — `visibleMembers`). */
  totalMembers?: number;
  /** Только группа сертификатов: сколько просроченных сертификатов (шаг «Сертификаты»). */
  expiredCertificates?: number;
}

/**
 * Предупреждения (SB120 — не запреты, §4):
 *   - `legal_required_doc_pdf` — обязательный документ (возврат, согласие)
 *     в виде PDF (SB140); оферта и политика PDF быть не могут (CHECK);
 *   - `legal_description_missing` — PDF или группа без описания (без него
 *     не опубликовать);
 *   - `legal_required_doc_unpublished` — оферта или политика ПДн не видны
 *     на сайте (плашка SB125);
 *   - `legal_member_not_published` — в ручной группе есть невидимые документы;
 *   - `legal_group_hidden_empty` — группа показывается, но пуста (SB16);
 *   - `legal_certificates_expired` — в группе сертификатов есть просроченные.
 */
export function legalDocumentWarnings(doc: LegalWarningsInput): LegalDocumentWarning[] {
  const out: LegalDocumentWarning[] = [];
  if (doc.kind === "PDF" && isLegalSystemSlug(doc.slug)) out.push({ code: "legal_required_doc_pdf" });
  if (doc.kind !== "TEXT" && (doc.description ?? "").trim().length === 0) out.push({ code: "legal_description_missing" });
  if (isLegalTextOnlySlug(doc.slug) && !isLegalDocumentVisible(doc)) out.push({ code: "legal_required_doc_unpublished" });
  if (doc.kind === "GROUP") {
    const visible = doc.visibleMembers ?? 0;
    const hidden = Math.max(0, (doc.totalMembers ?? visible) - visible);
    if (doc.groupSource !== "PRODUCER_CERTIFICATES" && hidden > 0) {
      out.push({ code: "legal_member_not_published", count: hidden });
    }
    if (doc.isPublished && visible === 0) out.push({ code: "legal_group_hidden_empty" });
    if ((doc.expiredCertificates ?? 0) > 0) out.push({ code: "legal_certificates_expired", count: doc.expiredCertificates });
  }
  return out;
}

// ── Файлы ───────────────────────────────────────────────────────────────────

/**
 * Имя файла для базы и подписи (§3.3, как обрезка в stored-file-response):
 * без управляющих символов и `/\`, пробелы схлопнуты, до 150 знаков (по
 * кодовым точкам, чтобы не резать суррогатную пару); пусто — «document.pdf».
 */
export function sanitizeLegalFileName(name: string): string {
  // Управляющие символы — по коду, а не регуляркой (no-control-regex).
  const cleaned = [...name]
    .map((ch) => {
      const code = ch.codePointAt(0) ?? 0;
      return code < 0x20 || code === 0x7f || ch === "/" || ch === "\\" ? " " : ch;
    })
    .join("")
    .replace(/\s+/g, " ")
    .trim();
  const points = [...cleaned];
  const cut = points.length > LEGAL_FILE_NAME_MAX_LENGTH ? points.slice(0, LEGAL_FILE_NAME_MAX_LENGTH).join("").trim() : cleaned;
  return cut.length > 0 ? cut : "document.pdf";
}

/** Размер файла по-русски: «820 КБ», «2,4 МБ» (десятичная запятая; 1 КБ = 1024 байт). */
export function formatLegalFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${Math.round(bytes)} Б`;
  // Округлённые 1024 КБ — уже мегабайты: «1 МБ», а не «1024 КБ».
  const kb = Math.max(1, Math.round(bytes / 1024));
  if (kb < 1024) return `${kb} КБ`;
  const rounded = Math.round((bytes / (1024 * 1024)) * 10) / 10;
  return `${String(rounded).replace(".", ",")} МБ`;
}
